import React, { FC, memo, useState } from 'react';

import classNames from 'classnames';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import DateRangeInput from '@zvuk-b2b/react-uikit/ui/DateInput/DateRangeInput';

import AddExternalRecipientsButton from '../ReportRequestForm/AddExternalRecipientsButton';
import AddressReportPointsSelect from '../ReportRequestForm/AddressReportPointsSelect';
import AudioReportActionButtons from '../ReportRequestForm/AudioReportActionButtons';
import ExternalReportRecipientsInput from '../ReportRequestForm/ExternalReportRecipientsInput';
import ReportRecipientsSelect from '../ReportRequestForm/ReportRecipientsSelect';
import { TEXTS } from './texts';
import { DATA_TEST_IDS, DATE_RANGE_DESKTOP_MONTHS_SHOWN, DATE_RANGE_MOBILE_MONTHS_SHOWN } from './consts';
import AddressReportTypeSelect from './AddressReportTypeSelect';
import PromoFieldShellContainer from './PromoFieldShell';
import { AUDIO_REPORT_SCOPE, PROMO_SELECTOR_LAYOUT } from './types';
import type { AudioAdCampaignsReportModalProps, PromoSelectorLayout } from './types';

import styles from './index.module.scss';

const AudioAdCampaignsReportModal: FC<AudioAdCampaignsReportModalProps> = ({
  calendarMaxDate,
  closeButtonText,
  endDate,
  externalRecipientEmails,
  isExternalRecipientsInputVisible,
  isMobile,
  isPromoDraftLoading,
  isRequestDisabled,
  isReportAddressesLoading,
  loading,
  modalTitle,
  minDate,
  onDateRangeChange,
  onExternalRecipientEmailsChange,
  onModalClose,
  onReportScopeChange,
  onRetryPromoDraftCreate,
  onRequestSubmit,
  onSelectedAddressIdsChange,
  onSelectedRecipientIdsChange,
  onShowExternalRecipientsInput,
  recipients,
  reportAddresses,
  reportAddressTags,
  reportScope,
  promoDraftError,
  promoDraftId,
  promoTotalCount,
  requestButtonText,
  selectedAddressIds,
  selectedPromoCount,
  selectedRecipientIds,
  setSelectedPromoCount,
  startDate,
}) => {
  const dateRangeMonthsShown = isMobile ? DATE_RANGE_MOBILE_MONTHS_SHOWN : DATE_RANGE_DESKTOP_MONTHS_SHOWN;
  const [isPromoSelectorOpen, setIsPromoSelectorOpen] = useState(false);
  const [promoSelectorLayout, setPromoSelectorLayout] = useState<PromoSelectorLayout>(PROMO_SELECTOR_LAYOUT.EXPANDED);

  return (
    <ModalScreen
      title={modalTitle}
      onClose={onModalClose}
      elementId={DATA_TEST_IDS.AUDIO_MODAL}
      containerClassName={classNames(styles.audioContainer, {
        [styles.audioContainerWithCollapsedPromoSelector]:
          reportScope === AUDIO_REPORT_SCOPE.PROMOS && !isPromoSelectorOpen,
        [styles.audioContainerWithPromoSelectorExpanded]:
          isPromoSelectorOpen && promoSelectorLayout === PROMO_SELECTOR_LAYOUT.EXPANDED,
        [styles.audioContainerWithPromoSelectorSingleResult]:
          isPromoSelectorOpen && promoSelectorLayout === PROMO_SELECTOR_LAYOUT.SINGLE_RESULT,
        [styles.audioContainerWithPromoSelectorEmpty]:
          isPromoSelectorOpen && promoSelectorLayout === PROMO_SELECTOR_LAYOUT.EMPTY,
      })}
      contentClassName={styles.audioContent}
      contentElementId={DATA_TEST_IDS.AUDIO_MODAL_CONTENT}
      containerActionButtonsClassName={classNames(styles.audioActionButtons, {
        [styles.audioActionButtonsWithCollapsedPromoSelector]:
          reportScope === AUDIO_REPORT_SCOPE.PROMOS && !isPromoSelectorOpen,
      })}
      actionButtons={
        <AudioReportActionButtons
          closeButtonText={closeButtonText}
          isRequestDisabled={isRequestDisabled}
          isRequestLoading={loading}
          requestButtonText={requestButtonText}
          onClose={onModalClose}
          onRequestSubmit={onRequestSubmit}
        />
      }>
      <div
        className={styles.audioDescription}
        data-testid={DATA_TEST_IDS.AUDIO_DESCRIPTION}>
        {TEXTS.REQUEST_AUDIO_REPORT_MODAL_CONTENT}
      </div>
      <div
        className={styles.audioDateRangeField}
        data-testid={DATA_TEST_IDS.AUDIO_DATE_RANGE_FIELD}>
        <label
          className={styles.audioFieldLabel}
          htmlFor={DATA_TEST_IDS.REPORT_DATE_RANGE_INPUT}
          data-testid={DATA_TEST_IDS.AUDIO_DATE_RANGE_LABEL}>
          {TEXTS.PERIOD_LABEL}
        </label>
        <DateRangeInput
          elementId={DATA_TEST_IDS.REPORT_DATE_RANGE_INPUT}
          startDate={startDate}
          endDate={endDate}
          onChange={onDateRangeChange}
          popperPlacement="bottom-start"
          minDate={minDate}
          maxDate={calendarMaxDate}
          label={TEXTS.PERIOD_LABEL}
          containerClassName={styles.audioDateRangeInput}
          calendarButtonClassName={styles.audioDateRangeCalendarButton}
          clearButtonClassName={styles.audioDateRangeClearButton}
          popperClassName={styles.audioDateRangePopper}
          showPreviousMonths={false}
          openToDate={isMobile ? minDate : startDate || calendarMaxDate}
          monthsShown={dateRangeMonthsShown}
          focusSelectedMonth={!isMobile}
          isClearable
          withPortal={isMobile}
        />
      </div>
      <AddressReportTypeSelect
        value={reportScope}
        onChange={onReportScopeChange}
      />

      {reportScope === AUDIO_REPORT_SCOPE.PROMOS && (
        <PromoFieldShellContainer
          draftError={promoDraftError}
          draftId={promoDraftId}
          isDraftLoading={isPromoDraftLoading}
          selectedCount={selectedPromoCount}
          totalCount={promoTotalCount}
          onDropdownOpenChange={setIsPromoSelectorOpen}
          onDropdownLayoutChange={setPromoSelectorLayout}
          onRetryDraftCreate={onRetryPromoDraftCreate}
          setSelectedCount={setSelectedPromoCount}
        />
      )}

      {reportScope === AUDIO_REPORT_SCOPE.ADDRESSES && (
        <AddressReportPointsSelect
          addresses={reportAddresses}
          isLoading={isReportAddressesLoading}
          tags={reportAddressTags}
          selectedAddressIds={selectedAddressIds}
          onChange={onSelectedAddressIdsChange}
        />
      )}

      <div
        className={styles.recipients}
        data-testid={DATA_TEST_IDS.AUDIO_RECIPIENTS}>
        <ReportRecipientsSelect
          afterTrigger={
            !isExternalRecipientsInputVisible ? (
              <AddExternalRecipientsButton onClick={onShowExternalRecipientsInput} />
            ) : undefined
          }
          recipients={recipients}
          selectedRecipientIds={selectedRecipientIds}
          onChange={onSelectedRecipientIdsChange}
        />

        <ExternalReportRecipientsInput
          isVisible={isExternalRecipientsInputVisible}
          emails={externalRecipientEmails}
          onChange={onExternalRecipientEmailsChange}
        />
      </div>
    </ModalScreen>
  );
};

export default memo(AudioAdCampaignsReportModal);
